import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useTranslation } from 'react-i18next';
import { databaseTimePreset } from '../../Utils/database_history_utils';

const useStyles = makeStyles((theme) => ({
  content: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' } },
  quick: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing(1) },
  dates: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2), '& input': { colorScheme: theme.palette.type } },
  trigger: { textTransform: 'none', maxWidth: '100%', '& .MuiButton-label': { whiteSpace: 'normal' } }
}));

export default function HistoryTimeRange({ filters, onChange, allowAll = false }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const mobile = useMediaQuery(useTheme().breakpoints.down('xs'));
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from: '', until: '' });
  const from = new Date(draft.from).getTime(), to = new Date(draft.until).getTime();
  const valid = Number.isFinite(from) && Number.isFinite(to) && to > from && to - from <= 180 * 86400000;
  const label = (span) => span === 'all' ? t('database_time_all') : Number(span) < 48 ?
    t('database_span_hours', { count: Number(span) }) : t('database_span_days', { count: Number(span) / 24 });
  const dateLabel = (text) => new Date(text).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  const selectedLabel = filters.timeSpan === 'custom' ? `${dateLabel(filters.from)} – ${dateLabel(filters.until)}` : label(filters.timeSpan);
  const choose = (span) => {
    onChange({ timeSpan: span, ...(span === 'all' ? { from: '', until: '' } : databaseTimePreset(Number(span))) });
    setOpen(false);
  };
  return <>
    <Button className={classes.trigger} variant="outlined" startIcon={<AccessTimeIcon />} endIcon={<ExpandMoreIcon />}
      aria-label={`${t('database_time_span')} ${selectedLabel}`} aria-haspopup="dialog" onClick={() => {
        setDraft(filters.timeSpan === 'all' ? databaseTimePreset(24) : { from: filters.from, until: filters.until }); setOpen(true);
      }}>{selectedLabel}</Button>
    <Dialog open={open} onClose={() => setOpen(false)} fullScreen={mobile} maxWidth="sm" fullWidth aria-labelledby="history-period-title">
      <DialogTitle id="history-period-title">{t('database_time_span')}</DialogTitle>
      <DialogContent className={classes.content}>
        <div className={classes.quick}>
          {[1, 6, 12, 24, 48, 168, 336, 720, 2160, 4320].map((hours) => <Button key={hours} color="primary"
            variant={String(hours) === filters.timeSpan ? 'contained' : 'outlined'} onClick={() => choose(String(hours))}>{label(hours)}</Button>)}
          {allowAll && <Button onClick={() => choose('all')}>{t('database_time_all')}</Button>}
        </div>
        <div className={classes.dates}>
          <Typography variant="subtitle2">{t('database_time_custom')}</Typography>
          <TextField id="database-from" label={t('database_from')} type="datetime-local" value={draft.from} InputLabelProps={{ shrink: true }}
            onChange={(event) => { const from = event.target.value; setDraft((old) => ({ ...old, from })); }} />
          <TextField id="database-until" label={t('database_until')} type="datetime-local" value={draft.until} InputLabelProps={{ shrink: true }}
            onChange={(event) => { const until = event.target.value; setDraft((old) => ({ ...old, until })); }} />
          {!valid && <Typography variant="caption" color="error">{t('database_invalid_dates')}</Typography>}
          <Button color="primary" variant="contained" disabled={!valid} onClick={() => {
            onChange({ timeSpan: 'custom', ...draft }); setOpen(false);
          }}>{t('database_apply_period')}</Button>
        </div>
        <Box gridColumn="1 / -1"><Typography variant="caption" color="textSecondary">{Intl.DateTimeFormat().resolvedOptions().timeZone}</Typography></Box>
      </DialogContent>
      <DialogActions><Button onClick={() => setOpen(false)}>{t('close')}</Button></DialogActions>
    </Dialog>
  </>;
}
